import api from './api';

const BASE = '/design-studio';

const designStudioService = {
  createProject: async (name, type = 'prototype', fidelity = 'high_fidelity', designSystemId = '') => {
    const response = await api.post(`${BASE}/projects`, {
      name,
      projectType: type,
      fidelity,
      designSystemId,
    });
    return response.data.data;
  },

  listProjects: async () => {
    const response = await api.get(`${BASE}/projects`);
    return response.data.data;
  },

  getProject: async (id) => {
    const response = await api.get(`${BASE}/projects/${id}`);
    return response.data.data;
  },

  /**
   * Stream design generation via SSE (fetch-based, supports auth headers).
   * Returns { cancel, readAll } where readAll is an async generator.
   *
   * Usage:
   *   const stream = designStudioService.generateDesign(id, prompt, history);
   *   for await (const event of stream.events()) { ... }
   */
  generateDesign: (projectId, prompt, conversationHistory = []) => {
    const token = localStorage.getItem('token');
    const port = process.env.REACT_APP_API_PORT || '8003';
    const baseURL = process.env.REACT_APP_API_URL || `http://localhost:${port}/api`;
    const url = `${baseURL}${BASE}/projects/${projectId}/generate/`;

    let abortController = new AbortController();

    const events = async function* () {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'text/event-stream',
          'Cache-Control': 'no-cache',
        },
        body: JSON.stringify({ prompt, conversationHistory }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        for (const line of chunk.split('\n')) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;
          try {
            yield JSON.parse(raw);
          } catch {
            // malformed chunk — skip
          }
        }
      }
    };

    return {
      events,
      cancel: () => abortController.abort(),
    };
  },

  updateFile: async (fileId, content) => {
    const response = await api.put(`${BASE}/files/${fileId}`, { content });
    return response.data.data;
  },

  deleteProject: async (id) => {
    const response = await api.delete(`${BASE}/projects/${id}`);
    return response.data;
  },

  applyEdits: async (fileId, edits) => {
    const response = await api.post(`${BASE}/files/${fileId}/edit`, { edits });
    return response.data.data;
  },

  getSketch: async (projectId) => {
    const response = await api.get(`${BASE}/projects/${projectId}/sketch`);
    return response.data.data;
  },

  updateSketch: async (fileId, content) => {
    const response = await api.put(`${BASE}/files/${fileId}`, { content });
    return response.data.data;
  },

  // ── Phase 7 — Project management ──────────────────────────────────────────

  updateProject: async (id, data) => {
    const response = await api.put(`${BASE}/projects/${id}`, data);
    return response.data.data;
  },

  duplicateProject: async (id) => {
    const response = await api.post(`${BASE}/projects/${id}/duplicate`);
    return response.data.data;
  },

  duplicateAsTemplate: async (id) => {
    const response = await api.post(`${BASE}/projects/${id}/duplicate-template`);
    return response.data.data;
  },

  listTemplates: async () => {
    const response = await api.get(`${BASE}/templates`);
    return response.data.data;
  },

  // ── Phase 9 — Export ──────────────────────────────────────────────────────

  exportProject: async (projectId, type) => {
    const token = localStorage.getItem('token');
    const port = process.env.REACT_APP_API_PORT || '8003';
    const baseURL = process.env.REACT_APP_API_URL || `http://localhost:${port}/api`;
    const url = `${baseURL}${BASE}/projects/${projectId}/export/${type}`;

    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error(`Export failed: HTTP ${response.status}`);

    const blob = await response.blob();
    const ext = type === 'zip' ? 'zip' : type === 'html' ? 'html' : 'json';
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `design-${projectId}.${ext}`;
    a.click();
    URL.revokeObjectURL(objectUrl);
  },

  // ── Phase 10 — Tweaks ─────────────────────────────────────────────────────

  analyzeDesign: (projectId) => {
    const token = localStorage.getItem('token');
    const port = process.env.REACT_APP_API_PORT || '8003';
    const baseURL = process.env.REACT_APP_API_URL || `http://localhost:${port}/api`;
    const url = `${baseURL}${BASE}/projects/${projectId}/analyze`;
    const abortController = new AbortController();

    const events = async function* () {
      const response = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
        signal: abortController.signal,
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        for (const line of chunk.split('\n')) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;
          try { yield JSON.parse(raw); } catch {}
        }
      }
    };
    return { events, cancel: () => abortController.abort() };
  },

  applyTweak: async (projectId, edits, fileId = null) => {
    const response = await api.post(`${BASE}/projects/${projectId}/tweaks/apply`, { edits, fileId });
    return response.data.data;
  },
};

export default designStudioService;
